import { useTranslation } from "react-i18next";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import { IVehicleDemand } from "../../models/stockSearch/IStockSearch";
import openInNewTab from "../../utils/openInNewTab";
import LoadingPulse from "../shared/LoadingPulse";

interface VehicleDemandBannerProps {
  demand?: IVehicleDemand;
  isLoading: boolean;
}

/**
 * How often parts for this car were looked for before it arrived: saved stock searches over the
 * last 180 days (by engine code and by make/model) and open part requests that match it. The
 * per-category split sits in the table's Demand column; this is the car-level total, including
 * the searches and requests that picked no category and so sit on no row.
 */
export default function VehicleDemandBanner({ demand, isLoading }: VehicleDemandBannerProps) {
  const { t } = useTranslation("jobs");

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600">
        <MagnifyingGlassIcon className="h-4 w-4" aria-hidden="true" />
        <LoadingPulse width="w-48" />
      </div>
    );
  }

  if (!demand) return null;

  const quiet = demand.totalSearches === 0 && demand.openPartRequests === 0;
  if (quiet) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-500">
        <MagnifyingGlassIcon className="h-4 w-4" aria-hidden="true" />
        <span>{t("No stock searches or part requests for this car in the last 180 days.")}</span>
      </div>
    );
  }

  const searchLine = demand.totalSearches === 1
    ? t("Searched once in the last 180 days")
    : t("Searched {{count}} times in the last 180 days", { count: demand.totalSearches });

  return (
    <div className="rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-900">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="flex items-center gap-2 font-semibold">
          <MagnifyingGlassIcon className="h-4 w-4" aria-hidden="true" />
          {searchLine}
        </span>
        {demand.totalSearches > 0 && (
          <span className="text-primary-800">
            {t("{{count}} by engine code", { count: demand.byEngineCode })}
            {" · "}
            {t("{{count}} by make/model", { count: demand.byMakeModel })}
            {demand.uncategorised > 0 && (
              <>
                {" · "}
                {t("{{count}} picked no category", { count: demand.uncategorised })}
              </>
            )}
          </span>
        )}
      </div>
      {demand.openPartRequests > 0 && (
        <p className="mt-1 text-primary-800">
          <button type="button" onClick={() => openInNewTab("/part-requests")} className="font-medium hover:underline">
            {demand.openPartRequests === 1
              ? t("1 open part request matches this car")
              : t("{{count}} open part requests match this car", { count: demand.openPartRequests })}
          </button>
          {demand.uncategorisedPartRequests > 0 && (
            <span>{" · "}{t("{{count}} without a category", { count: demand.uncategorisedPartRequests })}</span>
          )}
        </p>
      )}
    </div>
  );
}
